import api from './apiConfig';

// Get Course Tree with personalized stats (for learner)
// POST /frontsite-api/v1/learner/learning/course-tree
// Body: {} (uses learner's universal-learning-id from token)
export const getCourseTree = async () => {
  try {
    console.log('🌐 API CALL: getCourseTree - Starting request...');
    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/course-tree');
    const response = await api.post('/frontsite-api/v1/learner/learning/course-tree', {});
    console.log('🌐 API CALL: getCourseTree - Response received:', response);
    return {
      success: response.data?.status === 'ok',
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: getCourseTree - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get Lessons by Subcourse (with learner progress)
// POST /frontsite-api/v1/learner/learning/lessons-by-subcourse
// Body: { "data": { "subcourse-id": "uuid" } }
// Returns lessons with user-stats (practice progress) per lesson
export const getLessonsBySubcourse = async (subcourseId) => {
  try {
    console.log('🌐 API CALL: getLessonsBySubcourse - Starting request...');
    console.log('🌐 API CALL: subcourseId:', subcourseId);
    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/lessons-by-subcourse');
    console.log('🌐 API CALL: Body:', { data: { 'subcourse-id': subcourseId } });
    const response = await api.post('/frontsite-api/v1/learner/learning/lessons-by-subcourse', {
      data: {
        'subcourse-id': subcourseId,
      },
    });
    console.log('🌐 API CALL: getLessonsBySubcourse - Response received:', response);
    console.log('🌐 API CALL: Lessons data sample:', response.data?.data?.[0]); // Log sample lesson to see structure
    
    // Handle different response formats
    let lessonsData = response.data?.data;
    
    // If response is object with subcourse-id as key
    if (lessonsData && typeof lessonsData === 'object' && !Array.isArray(lessonsData)) {
      lessonsData = lessonsData[subcourseId] || Object.values(lessonsData)[0] || [];
    }
    
    // Ensure it's an array
    if (!Array.isArray(lessonsData)) {
      lessonsData = [];
    }
    
    return {
      success: response.data?.status === 'ok' || response.data?.status === 'success',
      data: lessonsData,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: getLessonsBySubcourse - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get Skills by Lesson
// POST /frontsite-api/v1/learner/learning/skills-by-lesson
// Body: { "data": { "lesson-id": "uuid" } }
export const getSkillsByLesson = async (lessonId) => {
  try {
    // CRITICAL: Log lessonId yang dikirim untuk memastikan berbeda per lesson
    console.log('🌐 API CALL: getSkillsByLesson - Starting request...');
    console.log('🌐 API CALL: lessonId (STRING):', String(lessonId));
    console.log('🌐 API CALL: lessonId (ORIGINAL):', lessonId);
    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/skills-by-lesson');
    const requestBody = { data: { 'lesson-id': lessonId } };
    console.log('🌐 API CALL: Body:', JSON.stringify(requestBody, null, 2));
    
    const response = await api.post('/frontsite-api/v1/learner/learning/skills-by-lesson', requestBody);
    
    // CRITICAL: Log response untuk memastikan data berbeda per lesson
    console.log('🌐 API CALL: getSkillsByLesson - Response received:', {
      lessonIdRequested: lessonId,
      status: response.data?.status,
      dataType: typeof response.data?.data,
      isArray: Array.isArray(response.data?.data),
      dataLength: Array.isArray(response.data?.data) ? response.data?.data.length : 'N/A',
      dataKeys: response.data?.data && typeof response.data?.data === 'object' && !Array.isArray(response.data?.data) 
        ? Object.keys(response.data?.data) 
        : 'N/A',
      // Log beberapa skill IDs untuk validasi
      firstFewSkillIds: Array.isArray(response.data?.data) 
        ? response.data?.data.slice(0, 3).map(s => s.id || s._id)
        : 'N/A'
    });
    
    return {
      success: response.data?.status === 'ok',
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    console.error('🌐 API CALL: getSkillsByLesson - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    console.error('🌐 API CALL: lessonId yang menyebabkan error:', lessonId);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};

// Get Notes by Skill
// POST /frontsite-api/v1/learner/learning/notes-by-skill
// Body: { "data": { "skill-id": "uuid" } }
export const getNotesBySkill = async (skillId, signal = null) => {
  try {
    console.log('🌐 API CALL: getNotesBySkill - Starting request...');
    console.log('🌐 API CALL: skillId:', skillId);
    console.log('🌐 API CALL: URL:', api.defaults.baseURL + '/frontsite-api/v1/learner/learning/notes-by-skill');
    console.log('🌐 API CALL: Body:', { data: { 'skill-id': skillId } });
    const response = await api.post('/frontsite-api/v1/learner/learning/notes-by-skill', {
      data: {
        'skill-id': skillId,
      },
    }, {
      signal: signal, // Support AbortController
    });
    console.log('🌐 API CALL: getNotesBySkill - Response received:', response);
    return {
      success: response.data?.status === 'ok',
      data: response.data?.data,
      message: response.data?.message,
    };
  } catch (error) {
    // Jangan log error kalau request di-cancel
    if (error.name === 'AbortError' || error.code === 'ECONNABORTED' || error.message?.includes('canceled')) {
      throw error; // Re-throw untuk di-handle di caller
    }
    console.error('🌐 API CALL: getNotesBySkill - Error:', error);
    console.error('🌐 API CALL: Error response:', error.response);
    return {
      success: false,
      message: error.response?.data?.message || error.message,
    };
  }
};
